import React, { forwardRef, useEffect, useState } from 'react';

import { cn } from '../../../../lib/utils';

export type CellNav = 'up' | 'down' | 'left' | 'right';

/** Accepts "3", "3.5", "3,5", "3:30", "3h30". Returns null when the input makes no sense. */
export function parseHours(raw: string): number | null {
  const value = raw.trim().replace(',', '.');
  if (!value) return 0;

  const hoursAndMinutes = value.match(/^(\d{1,2})\s*[:h]\s*(\d{1,2})$/i);
  if (hoursAndMinutes) {
    const minutes = Number(hoursAndMinutes[2]);
    if (minutes > 59) return null;
    return Number(hoursAndMinutes[1]) + minutes / 60;
  }

  const decimal = Number(value);
  if (!Number.isFinite(decimal) || decimal < 0 || decimal > 24) return null;
  return decimal;
}

/** Zero renders as nothing — an empty cell reads faster than a column of "0.00". */
export function formatHours(value: number): string {
  if (!value) return '';
  return (Math.round(value * 100) / 100).toFixed(2).replace(/\.?0+$/, '');
}

export function formatTotal(value: number): string {
  return formatHours(value) || '0';
}

interface HourCellProps {
  value: number;
  disabled?: boolean;
  isToday?: boolean;
  isWeekend?: boolean;
  onCommit: (value: number) => void;
  onNav: (direction: CellNav) => void;
}

const HourCell = forwardRef<HTMLInputElement, HourCellProps>(
  ({ value, disabled, isToday, isWeekend, onCommit, onNav }, ref) => {
    const [draft, setDraft] = useState(() => formatHours(value));
    const [invalid, setInvalid] = useState(false);

    // Value can change from outside the cell — copying a week, switching weeks, resetting.
    useEffect(() => {
      setDraft(formatHours(value));
    }, [value]);

    const commit = () => {
      const parsed = parseHours(draft);
      if (parsed === null) {
        setInvalid(true);
        setDraft(formatHours(value));
        window.setTimeout(() => setInvalid(false), 700);
        return;
      }
      setDraft(formatHours(parsed));
      if (parsed !== value) onCommit(parsed);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
      const atEnd = input.selectionStart === draft.length && input.selectionEnd === draft.length;

      switch (event.key) {
        case 'Enter':
        case 'ArrowDown':
          event.preventDefault();
          commit();
          onNav('down');
          break;
        case 'ArrowUp':
          event.preventDefault();
          commit();
          onNav('up');
          break;
        case 'ArrowLeft':
          if (atStart) {
            event.preventDefault();
            commit();
            onNav('left');
          }
          break;
        case 'ArrowRight':
          if (atEnd) {
            event.preventDefault();
            commit();
            onNav('right');
          }
          break;
        case 'Escape':
          event.preventDefault();
          setDraft(formatHours(value));
          input.blur();
          break;
        default:
          break;
      }
    };

    if (disabled) {
      return (
        <div
          className={cn(
            'flex h-9 items-center justify-center px-1 text-sm tabular-nums',
            value ? 'text-foreground' : 'text-muted-foreground/40',
            isWeekend && 'text-muted-foreground',
          )}
        >
          {formatHours(value) || '·'}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        value={draft}
        inputMode="decimal"
        aria-invalid={invalid}
        onChange={event => setDraft(event.target.value)}
        onFocus={event => event.target.select()}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={cn(
          'h-9 w-full rounded-sm border border-transparent bg-transparent px-1 text-center text-sm tabular-nums',
          'transition-colors placeholder:text-muted-foreground/30',
          'hover:border-border focus:border-primary focus:outline-none',
          isToday && 'font-medium',
          isWeekend && 'text-muted-foreground',
          invalid && 'border-destructive bg-destructive/10 text-destructive',
        )}
        placeholder="·"
      />
    );
  },
);

HourCell.displayName = 'HourCell';

export default HourCell;
